// TODO: Color values should get swapped with constants.

import { IResponsiveSizes } from "../hooks/useResponsiveSizes";
import {
  immersightBlue,
  immersightBlue50,
  immersightBlue75,
  immersightBlueLighter1
} from "./colors";

/**
 * Function to create the css properties that are needed to render the download related components.
 *
 * @param {IResponsiveSizes} sizes the object that is returned from the useResponsiveSizes() hook.
 * @returns {React.CSSProperties} object containing the styles to get applied to download related components.
 */
export const getDownloadStyles = (sizes: IResponsiveSizes) => ({
  box: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    fontFamily: "Roboto",
    width: "100%",
    height: "100%",
    userSelect: "none",
  },
  headerTypography: {
    fontWeight: "500",
    color: "black",
    margin: {
      xs: ".2rem .25rem .3rem",
      sm: ".25rem .25rem .35rem",
      md: ".3rem .25rem .4rem",
      lg: ".4rem .25rem .5rem",
      xl: ".5rem .25rem .6rem",
    },
    padding: sizes.isHeightOver800 ? ".2rem" : ".05rem",
    fontFamily: "Roboto",
    fontSize: {
      xs: "2.2rem",
      sm: "2.3rem",
      md: "2.5rem",
      lg: "2.7rem",
      xl: "2.9rem",
    },
    lineWrap: "nowrap",
    wordBreak: "keep-all",
    overflow: "visible",
    whiteSpace: "nowrap",
  },
  gridContainer: {
    height: "100%",
    width: "100%",
    alignSelf: "center",
    justifySelf: "center",
  },
  gridItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  iconBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "65%",
    gap: "15%",
  },
  icon: {
    color: "#87d3ff",
    paddingLeft: "1.5rem",
    paddingRight: "1.5rem",
    paddingTop: "1.5rem",
    paddingBottom: "1.5rem",
    backgroundColor: "white",
    margin: sizes.iconMargin,
    marginRight: ".1rem",
    borderRadius: "1rem",
    fontSize: `calc(${sizes.fontSize}*.95)`,
    width: `calc(${sizes.fontSize}*.77)`,
    height: `calc(${sizes.fontSize}*.77)`,
    minHeight: `calc(${sizes.fontSize}*.77)`,
    minWidth: `calc(${sizes.fontSize}*.77)`,
  },
  typography: {
    fontWeight: "600",
    m: ".25rem 0 .45rem",
    fontSize: `calc(${sizes.fontSize}*.27)`,
  },
  typography_inButton: {
    fontWeight: "600",
    m: ".25rem 0 .45rem",
    fontSize: `calc(${sizes.fontSize}*.5)`,
    color: "#ffffff",
  },
  tabs: {
    color: "#9b9b9b", //TODO: use theme
    "&.Mui-selected": {
      color: immersightBlue,
    },
    "&.MuiTabs-indicator": {
      backgroundColor: immersightBlue,
    },
  },
  submodal_container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: 0,
    width: "100%",
    height: "100%",
  },
  submodal_topbar: {
    display: "flex",
    alignItems: "center",
    width: "fit-content",
  },
  settings_elements_container: {
    display: "flex",
    flexDirection: "column",
    width: "fit-content", // Adjust to gap of children flexboxes
    minWidth: "75%", // If gap of children is smaller than this, use 80%
    flexGrow: "1",
    justifyContent: "center",
    gap: "1px",
    "&.MuiContainer-root": {
      padding: 0,
    },
  },
  settings_element: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "2rem", // Minimum gap, see minWidth and width of parent
    alignItems: "center",
    marginTop: ".15rem",
    "& input": {
      backgroundColor: immersightBlue50,
    },
    "& > span": {
      backgroundColor: immersightBlueLighter1,
    },
    "&:hover > span": {
      backgroundColor: immersightBlueLighter1,
    },
  },
  textFields: {
    flexGrow: 1,
    flexShrink: 1,
    width: "0px",
  },
  selectField: {
    marginTop: ".2rem",
    "& > div": {
      padding: ".3rem 2.0rem .3rem .8rem !important",
    },
    "&:hover > div": {
      backgroundColor: immersightBlue50,
    },
  },
  menuItem: {
    padding: ".2rem .6rem",
    textAlign: "center",
  },
  colorSelector: {
    width: "5rem",
    "& .MuiInputBase-input": {
      cursor: "pointer",
      borderRadius: "0.25rem",
    },
    "& .MuiOutlinedInput-input": {
      height: "2rem",
      padding: 0,
    },
  },
});

/**
 * Function to create the css properties that are needed to render the input adornment for the multi directional number input.
 *
 * @returns {React.CSSProperties} object containing the styles for the icon.
 */
export const getNumberInputAdornmentStyles = () => ({
  cursor: "pointer",
  transition: "ease-in-out .5s all",
  border: "solid 1px white !important",
  fill: "rgba(75, 75, 75, 1)",
  color: "rgba(75, 75, 75, 1)",
  stroke: "rgba(75, 75, 75, 1)",
  "&:hover": {
      transition: "ease-in-out .5s all",
      border: `solid 1px ${immersightBlue75} !important`,
  }
});
